import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { SR_ANNOUNCE_DELAY_MS } from 'platform/site-wide/autosuggest/constants';
import { LocationValidationMessage } from '../../../constants';

function AddressInputError({ showError, errorId, errorType }) {
  const message =
    errorType === 'invalid'
      ? LocationValidationMessage.INVALID
      : LocationValidationMessage.EMPTY;

  // Announcement is delayed so it doesn't collide with the submit focus shift:
  // JAWS drops role="alert" updates that fire during a focus change, leaving it
  // reading only the field label (CMS #22751).
  const [srMessage, setSrMessage] = useState('');
  const timerRef = useRef(null);

  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    if (!showError) {
      setSrMessage('');
      return undefined;
    }

    timerRef.current = setTimeout(() => {
      setSrMessage(message);
    }, SR_ANNOUNCE_DELAY_MS);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [showError, message]);

  return (
    <>
      {/* Visible error; hidden from assistive tech so it isn't duplicated in
          the reading order — the live region below is what's announced. */}
      {showError && (
        <span
          className="usa-input-error-message"
          id={errorId}
          aria-hidden="true"
        >
          <span className="usa-sr-only sr-only">Error</span>
          <span className="usa-error-message">{message}</span>
        </span>
      )}
      {/* Always-rendered assertive live region (only its text changes), so the
          screen reader has it buffered and announces insertions reliably. */}
      <div
        className="usa-sr-only sr-only"
        role="alert"
        data-testid="address-error-live-region"
      >
        {srMessage}
      </div>
    </>
  );
}

AddressInputError.propTypes = {
  errorId: PropTypes.string,
  errorType: PropTypes.oneOf(['empty', 'invalid']),
  showError: PropTypes.bool,
};

export default AddressInputError;
